const calculateStepProgress = (
    currentStep: number,
    targetStep: number,
    currentProgress: number
): number => {
    if (currentStep === targetStep) {
        return currentProgress;
    }
    if (currentStep < targetStep) {
        return 0;
    }
    return 100;
};

export const calculateAllStepProgress = (
    currentStep: number,
    maxStmsToAssign: number,
    currentStmsAssigned: number,
    totalSteps: number = 4
): number[] => {
    const currentStepProgress = Math.min(
        100,
        Math.round(
            ((maxStmsToAssign - currentStmsAssigned) / maxStmsToAssign) * 100
        )
    );

    return Array.from({ length: totalSteps }, (_, i) => 
        calculateStepProgress(currentStep, i + 1, currentStepProgress)
    );
}; 

export const calculateOverallProgress = (
    status: string,
    bins: Record<string, { stms: any[]; max: number }>,
    nBucket: number,
    hasMidBucketRanking?: boolean
): number => {
    // Define step weights based on the total statements in each step
    const stepWeights = {
        'S1_42': 42, // Initial 42 statements (11+20+11)
        'S2_11_LOW': 11, // 11 least important statements (2+4+5)
        'S3_11_HIGH': 11, // 11 most important statements (5+4+2)
        'S3_20_MID': 20, // 20 middle statements (6+8+6)
        'S4_20_MID': 20, // 20 middle statements (6+8+6)
        'S4_11_HIGH': 11, // 11 most important statements (5+4+2)
        'FINAL_OVERVIEW': 0, // No additional statements to assign
        'DONE_SENDING': 0, // No additional statements to assign
    };

    // Calculate total statements based on the actual flow path
    let totalStatements = 0;
    if (nBucket === 7 && hasMidBucketRanking) {
        // Flow: S1_42 -> S2_11_LOW -> S3_11_HIGH -> S4_20_MID
        totalStatements = stepWeights['S1_42'] + stepWeights['S2_11_LOW'] + stepWeights['S3_11_HIGH'] + stepWeights['S4_20_MID'];
    } else if (nBucket === 9) {
        // Flow: S1_42 -> S2_11_LOW -> S3_20_MID -> S4_11_HIGH
        totalStatements = stepWeights['S1_42'] + stepWeights['S2_11_LOW'] + stepWeights['S3_20_MID'] + stepWeights['S4_11_HIGH'];
    } else {
        // Flow: S1_42 -> S2_11_LOW -> S3_11_HIGH (no mid bucket ranking)
        totalStatements = stepWeights['S1_42'] + stepWeights['S2_11_LOW'] + stepWeights['S3_11_HIGH'];
    }
    
    // Calculate completed statements from previous steps
    let completedStatements = 0;
    let currentStepStatements = 0;
    
    // Determine which steps are completed based on status
    const completedSteps = [];
    switch (status) {
        case 'S1_42':
            // No steps completed yet - we're still working on S1_42
            break;
        case 'S2_11_LOW':
            completedSteps.push('S1_42');
            break;
        case 'S3_11_HIGH':
            completedSteps.push('S1_42');
            completedSteps.push('S2_11_LOW');
            break;
        case 'S3_20_MID':
            completedSteps.push('S1_42');
            completedSteps.push('S2_11_LOW');
            break;
        case 'S4_20_MID':
            completedSteps.push('S1_42');
            completedSteps.push('S2_11_LOW');
            completedSteps.push('S3_11_HIGH');
            break;
        case 'S4_11_HIGH':
            completedSteps.push('S1_42');
            completedSteps.push('S2_11_LOW');
            completedSteps.push('S3_20_MID');
            break;
        case 'FINAL_OVERVIEW':
        case 'DONE_SENDING':
            // All steps are completed
            completedSteps.push('S1_42');
            completedSteps.push('S2_11_LOW');
            if (nBucket === 7 && hasMidBucketRanking) {
                completedSteps.push('S3_11_HIGH');
                completedSteps.push('S4_20_MID');
            } else if (nBucket === 9) {
                completedSteps.push('S3_20_MID');
                completedSteps.push('S4_11_HIGH');
            } else {
                completedSteps.push('S3_11_HIGH');
            }
            break;
    }
    
    // Sum completed statements
    completedStatements = completedSteps.reduce((sum, step) => sum + stepWeights[step as keyof typeof stepWeights], 0);
    
    // Calculate current step progress
    if (status !== 'FINAL_OVERVIEW' && status !== 'DONE_SENDING') {
        const toAssignCount = bins.to_assign?.stms?.length || 0;
        const toAssignMax = bins.to_assign?.max || 0;
        const currentStepMax = toAssignMax;
        const currentStepAssigned = currentStepMax - toAssignCount;
        
        if (currentStepMax > 0) {
            currentStepStatements = Math.round((currentStepAssigned / currentStepMax) * stepWeights[status as keyof typeof stepWeights]);
        }
    }
    
    // Calculate overall progress percentage
    const totalProgress = completedStatements + currentStepStatements;
    const overallProgress = Math.min(100, Math.round((totalProgress / totalStatements) * 100));
    
    return overallProgress;
}; 